import { useEffect } from 'react';

/**
 * Improves text selection by adding an additional div where the mouse was
 * clicked. This reduces flickering of the content if the mouse is slowly
 * dragged up or down.
 * Taken from /pdfjs/web/text_layer_builder.js #bindMouse #enableGlobalSelectionListener
 */
export const usePDFTextSelectionListener = () => {
  useEffect(() => {
    let prevRange: Range | null = null;

    const onSelectionChange = () => {
      const selection = document.getSelection();
      if (!selection || !selection.rangeCount) return;

      // Even though the spec says that .rangeCount should be 0 or 1, Firefox
      // creates multiple ranges when selecting across multiple pages.
      // Make sure to collect all the .textLayer elements where the selection
      // is happening.
      const activeTextLayers = new Set();
      const textLayerDivs = document.querySelectorAll('.pdfTextLayer');
      for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i);
        for (let i = 0; i < textLayerDivs.length; i++) {
          const textLayerDiv = textLayerDivs[i];
          if (!activeTextLayers.has(textLayerDiv) && range.intersectsNode(textLayerDiv)) {
            activeTextLayers.add(textLayerDiv);
            const endOfContentDiv = textLayerDiv.querySelector('.pdfEndOfContent');
            endOfContentDiv?.classList.add('active');
          }
        }
      }

      for (let i = 0; i < textLayerDivs.length; i++) {
        const textLayerDiv = textLayerDivs[i];
        const endOfContentDiv = textLayerDiv.querySelector('.pdfEndOfContent');
        if (activeTextLayers.has(textLayerDiv)) {
          endOfContentDiv?.classList.add('active');
        } else {
          endOfContentDiv?.classList.remove('active');
        }
      }

      // In non-Firefox browsers, when hovering over an empty space (thus,
      // on .endOfContent), the selection will expand to cover all the
      // text between the current selection and .endOfContent. By moving
      // .endOfContent to right after (or before, depending on which side
      // of the selection the user is moving), we limit the selection jump
      // to at most cover the enteirety of the <span> where the selection
      // is being modified.
      const range = selection.getRangeAt(0);
      const modifyStart =
        prevRange &&
        (range.compareBoundaryPoints(Range.END_TO_END, prevRange) === 0 ||
          range.compareBoundaryPoints(Range.START_TO_END, prevRange) === 0);
      let anchor = modifyStart ? range.startContainer : range.endContainer;
      if (anchor.nodeType === Node.TEXT_NODE && anchor.parentNode) {
        anchor = anchor.parentNode;
      }

      if (!anchor.parentElement) return;

      const parentTextLayer = anchor.parentElement.closest('.pdfTextLayer');
      if (!parentTextLayer || !(parentTextLayer instanceof HTMLDivElement)) return;

      const endDiv = parentTextLayer?.querySelector('.pdfEndOfContent');
      if (!!endDiv && endDiv instanceof HTMLDivElement) {
        endDiv.style.width = parentTextLayer.style.width;
        endDiv.style.height = parentTextLayer.style.height;
        anchor.parentElement.insertBefore(endDiv, modifyStart ? anchor : anchor.nextSibling);
      }

      prevRange = range.cloneRange();
    };

    document.addEventListener('selectionchange', onSelectionChange);

    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, []);
};
